import { ActionIcon, Flex, List, SegmentedControl, Table, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, formatDayMonth, todayISODate } from '../helpers/dateUtils';
import useFetch from '../helpers/useFetch';
import useIsSuperAdmin from '../helpers/useIsSuperAdmin';
import classes from './AccountUsage.module.css';
import BotClassificationNotMeasured from './BotClassificationNotMeasured';
import ErrorMessage from './ErrorMessage';
import MetricsSelector from './MetricsSelector';
import Spinner from './Spinner';
import SuperAdminBadge from './SuperAdmin/SuperAdminBadge';

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, start, end) => {
  return fetch(`/api/admin/accounts/${accountId}/usage/${encodeURIComponent(start)}/${encodeURIComponent(end)}`);
}, 300);

const botExcludedPercent = ({ activeProfiles, activeProfilesCorrected }) => {
  const billed = Number(activeProfiles);
  if (!billed || activeProfilesCorrected === undefined) {
    return null;
  }
  return Math.min(100, Math.max(0, Math.round(100 * (1 - Number(activeProfilesCorrected) / billed))));
};

const ColumnHelp = ({ text, label }) => (
  <Tooltip label={text} multiline w={280} withArrow position="top" events={{ hover: true, focus: true, touch: true }}>
    <ActionIcon variant="subtle" color="gray" size="xs" radius="xl" aria-label={label} className={classes.helpIcon}>
      <IconInfoCircle size={14} />
    </ActionIcon>
  </Tooltip>
);

const SnapshotDate = ({ at, zone, peak }) =>
  at ? <Text size="xs" c="dimmed">{`${peak ? 'peak, ' : ''}on ${formatDayMonth(at, zone)}`}</Text> : null;

const AccountUsage = () => {
  const initialPeriodDays = 30;
  const localTimezone = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ')[2];
  const navigate = useNavigate();
  const location = useLocation();

  const fetch = useFetch();
  const isSuperAdmin = useIsSuperAdmin();
  const [usage, setUsage] = useState();
  const [error, setError] = useState();
  const [period, setPeriod] = useState();

  const { account } = useSelector((state) => state.account);
  const user = useSelector((state) => state.auth.user);

  const { accountId } = useParams();
  const accountDomains = user.userRoles.find((r) => r.account?.id === account.id).account.domains;

  useEffect(() => {
    const filterDefaultValues = {
      start: daysAgoISODate(initialPeriodDays - 1),
      end: todayISODate(),
      useUTC: 'true',
    };

    const parsed = queryString.parse(location.search, { arrayFormat: 'bracket' });
    const periodFromUrl = { ...filterDefaultValues, ...parsed };
    setPeriod({
      ...parsed,
      start: periodFromUrl.start,
      end: periodFromUrl.end,
      useUTC: periodFromUrl.useUTC === 'true',
    });
  }, [location]);

  // Interpret the ISO date strings in the selected timezone
  const zone = period?.useUTC ? 'UTC' : 'local';

  useEffect(() => {
    if (!period) return;

    setUsage(undefined);

    const startTs = dateToStartOfDay(period.start, zone);
    const endTs = dateToEndOfDay(period.end, zone);

    statsDebounced(fetch, accountId, startTs, endTs)
      .then(({ usage }) => {
        setUsage(usage);
      })
      .catch(setError);
  }, [accountId, period, zone, fetch]);

  if (!period) {
    return '';
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  // Nothing enforces that these match the column counts in the row below them.
  const audienceColumns = isSuperAdmin ? 4 : 3;
  const activityColumns = 9;

  const timezoneLabel = period.useUTC ? 'GMT' : localTimezone;
  const strategy = account.monthlyActiveUsersCalculationStrategy;
  const isPeriodMax = strategy === 'periodMax';
  const isPeriodEnd = strategy === 'periodEnd';
  const activeProfilesWindow = 'Profiles with at least one page view in the 30 days before the date shown.';
  const activeProfilesBilling = isPeriodMax
    ? ' Your plan is billed on the highest value during the billing period.'
    : isPeriodEnd
      ? ' Your plan is billed on the value at the end of the billing period.'
      : '';
  const activeProfilesHelp = `${activeProfilesWindow}${activeProfilesBilling}`;

  const updatePeriod = (update) => {
    const updated = { ...period, ...update };
    // Preserve existing query params (like tab=usage) while updating date/timezone params
    const currentParams = queryString.parse(location.search, { arrayFormat: 'bracket' });
    navigate({
      search: queryString.stringify(
        {
          ...currentParams,
          start: updated.start,
          end: updated.end,
          useUTC: updated.useUTC ? 'true' : 'false',
        },
        { arrayFormat: 'bracket' }
      ),
    });
  };

  return (
    <>
      <Flex my="md" justify="space-between" align="center" wrap="wrap" gap="md">
        <MetricsSelector
          startDate={period.start}
          endDate={period.end}
          setStartDate={(start) => updatePeriod({ start })}
          setEndDate={(end) => updatePeriod({ end })}
          setDateRange={(start, end) => updatePeriod({ start, end })}
          rowSize="12"
        />
        <SegmentedControl
          value={String(period.useUTC)}
          onChange={(value) => updatePeriod({ useUTC: value === 'true' })}
          data={[
            { label: 'GMT timezone', value: 'true' },
            { label: `${localTimezone} (local timezone)`, value: 'false' },
          ]}
        />
      </Flex>

      {usage ? (
        <>
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th visibleFrom="md" />
                <Table.Th colSpan={audienceColumns}>Audience size</Table.Th>
                <Table.Th colSpan={activityColumns} className={classes.groupStart}>
                  {`Activity, ${formatDayMonth(period.start, zone)} – ${formatDayMonth(period.end, zone)} (${timezoneLabel})`}
                </Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th visibleFrom="md">Shop</Table.Th>
                <Table.Th>
                  Profiles{' '}
                  <ColumnHelp text="Total profiles stored for the shop on the date shown." label="About profiles" />
                </Table.Th>
                <Table.Th>
                  Active profiles (30-day) <ColumnHelp text={activeProfilesHelp} label="About active profiles" />
                </Table.Th>
                <Table.Th>
                  Subscribers{' '}
                  <ColumnHelp
                    text="Profiles you can reach by email or push notification on the date shown. Plans priced per subscriber are billed on the value at the end of the billing period."
                    label="About subscribers"
                  />
                </Table.Th>
                {isSuperAdmin && (
                  <Table.Th>
                    Active profiles, bots excluded <SuperAdminBadge iconOnly show />
                  </Table.Th>
                )}
                <Table.Th className={classes.groupStart}>Page Views</Table.Th>
                <Table.Th>Emails sent</Table.Th>
                <Table.Th>Push notifications sent</Table.Th>
                <Table.Th>Text messages sent</Table.Th>
                <Table.Th>Viber messages sent</Table.Th>
                <Table.Th>Viber message deliveries</Table.Th>
                <Table.Th>WhatsApp messages sent</Table.Th>
                <Table.Th>WhatsApp message deliveries</Table.Th>
                <Table.Th>Assisted revenue</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {accountDomains.map((d) => {
                const domainUsage = usage[d.id.toString()] || {};
                return (
                  <Table.Tr key={d.id}>
                    <Table.Td visibleFrom="md">
                      <strong>{d.name}</strong>
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.profiles === undefined ? '-' : domainUsage.profiles}
                      <SnapshotDate at={domainUsage.profilesAt} zone={zone} />
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.activeProfiles === undefined ? '-' : domainUsage.activeProfiles}
                      <SnapshotDate at={domainUsage.activeProfilesAt} zone={zone} peak={isPeriodMax} />
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.subscribers === undefined ? '-' : domainUsage.subscribers}
                      <SnapshotDate at={domainUsage.subscribersAt} zone={zone} />
                    </Table.Td>
                    {isSuperAdmin && (
                      <Table.Td>
                        {domainUsage.activeProfilesCorrected === undefined ? (
                          domainUsage.botClassification?.measured === false ? (
                            <BotClassificationNotMeasured classification={domainUsage.botClassification} />
                          ) : (
                            '-'
                          )
                        ) : (
                          <>
                            {domainUsage.activeProfilesCorrected}
                            {botExcludedPercent(domainUsage) !== null && (
                              <Text span size="xs" c="dimmed">{` (${botExcludedPercent(domainUsage)}% bots)`}</Text>
                            )}
                          </>
                        )}
                      </Table.Td>
                    )}
                    <Table.Td className={classes.groupStart}>
                      {domainUsage.pageViews === undefined ? '-' : domainUsage.pageViews}
                    </Table.Td>
                    <Table.Td>{domainUsage.emailsSent === undefined ? '-' : domainUsage.emailsSent}</Table.Td>
                    <Table.Td>
                      {domainUsage.pushNotificationsSent === undefined ? '-' : domainUsage.pushNotificationsSent}
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.textMessagesSent === undefined ? '-' : domainUsage.textMessagesSent}
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.viberMessagesSent === undefined ? '-' : domainUsage.viberMessagesSent}
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.viberMessageDeliveries === undefined ? '-' : domainUsage.viberMessageDeliveries}
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.whatsappMessagesSent === undefined ? '-' : domainUsage.whatsappMessagesSent}
                    </Table.Td>
                    <Table.Td>
                      {domainUsage.whatsappMessageDeliveries === undefined
                        ? '-'
                        : domainUsage.whatsappMessageDeliveries}
                    </Table.Td>
                    <Table.Td>
                      <strong>
                        {domainUsage.generatedRevenue === undefined
                          ? '-'
                          : domainUsage.generatedRevenue.toLocaleString(navigator.language, {
                              style: 'currency',
                              currency: d.currency,
                            })}
                      </strong>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <List size="sm" c="dimmed" spacing={4} mt="sm">
            <List.Item>Audience size is a point-in-time snapshot, not a total for the selected period.</List.Item>
            <List.Item>
              Activity columns count everything from the start of the first day to the end of the last day, in the
              selected timezone. Choose GMT to match your invoice, or {localTimezone} to match other reports in the
              platform.
            </List.Item>
            <List.Item>
              Assisted revenue: the value of purchased products credited to a Releva campaign, product block, banner,
              search, sales agent or Facebook/Google ad.
            </List.Item>
          </List>
        </>
      ) : (
        <Spinner />
      )}
    </>
  );
};
export default AccountUsage;
